import { useRouteContext } from "@tanstack/react-router";
import { Controller, UseFormReturn } from "react-hook-form";
import { Container } from "react-bootstrap";

import FormFieldRow from "@/components/shared/forms/FormFieldRow";
import Select from "@/components/shared/forms/Select";
import TextInput from "@/components/shared/forms/TextInput";
import DateInput from "@/components/shared/forms/DateInput";
import ReadOnlyDateInput from "@/components/shared/forms/ReadOnlyDateInput";
import ReadOnlySelect from "@/components/shared/forms/ReadOnlySelect";
import SearchComboBox, {
  type ComboBoxOption,
} from "@/components/shared/forms/SearchComboBox";
import { type CLEN } from "@/models.ts";
import { useCoreData } from "@/queries.ts";
import { FaChalkboardTeacher } from "react-icons/fa";
import { useCLENFormLogic } from "../child/useCLENFormLogic";

export default function CLENAddEditForm({
  selectedCLEN,
  form,
  clenDefaults,
  childOptions = [],
}: {
  selectedCLEN: CLEN | undefined;
  form: UseFormReturn<CLEN, any, CLEN>;
  clenDefaults: Partial<CLEN> | undefined;
  childOptions?: ComboBoxOption[];
}) {
  const coreData = useCoreData();
  const leaContext = useRouteContext({ from: "/$leaid" });
  const viewOnly = !leaContext.hasRole("CLENEdit");
  const tooltipMap = leaContext.coreData.tooltipMap.get("CLEN");

  const {
    register,
    control,
    setValue,
    formState: { errors },
  } = form;

  const { watchedFields, validators } = useCLENFormLogic(control);

  const {
    watchPreschoolCode,
    watchProgramYear,
    watchLocalClassroomId,
    watchCSPPID,
    watchLocalChildId,
  } = watchedFields;

  const { validateEnrollmentStart, validateEnrollmentEnd } = validators;

  // Disable child field when view-only or editing an existing record
  const isChildFieldDisabled = viewOnly || !!selectedCLEN?.LocalChildId;

  const preschoolOptions = leaContext
    .currentOrgPreschoolsByProgramYear(watchProgramYear)
    .map((x) => {
      return {
        Text: x.PreschoolName,
        Value: x.PreschoolCDSCode,
      };
    });

  return (
    <Container
      className="ps-3 pe-3 overview-container"
      style={{ borderTop: "none", borderRadius: 0 }}
    >
      <div
        className="form-card-modern mb-3"
        style={!isChildFieldDisabled ? { paddingBottom: 180 } : undefined}
      >
        <div className="section-header">
          <FaChalkboardTeacher className="section-icon" />
          <h5 className="section-title">Classroom Enrollment</h5>
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="PreschoolCode"
              labelText="Preschool"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) =>
                viewOnly ? (
                  <Select
                    id={id}
                    disabled
                    value={watchPreschoolCode}
                    {...register(id, { required: "Preschool is required" })}
                    options={preschoolOptions}
                  />
                ) : (
                  <ReadOnlySelect
                    id={id}
                    control={control}
                    name={name}
                    rules={{ required: "Preschool is required" }}
                    options={preschoolOptions}
                  />
                )
              }
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="ProgramYear"
              labelText="Program Year"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) =>
                !!selectedCLEN?.ProgramYear && !viewOnly ? (
                  <ReadOnlySelect
                    id={id}
                    control={control}
                    name={name}
                    rules={{ required: "Program Year is required" }}
                    options={coreData.programYearList}
                  />
                ) : (
                  <Select
                    id={id}
                    disabled={viewOnly}
                    {...register(id, { required: "Program Year is required" })}
                    options={coreData.programYearList}
                  />
                )
              }
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="CSPPID"
              labelText="CSPPID"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  readOnly={!!selectedCLEN?.CSPPID}
                  maxLength={50}
                  value={watchCSPPID}
                  {...register(id)}
                />
              )}
            </FormFieldRow>
          </div>
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="LocalChildId"
              labelText="Local Child ID (CICN)"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id) =>
                isChildFieldDisabled ? (
                  <TextInput
                    id={id}
                    disabled={viewOnly}
                    readOnly
                    maxLength={50}
                    value={watchLocalChildId}
                    {...register(id, {
                      required: "Local Child ID (CICN) is required",
                    })}
                  />
                ) : (
                  <Controller
                    name="LocalChildId"
                    control={control}
                    rules={{ required: "Local Child ID (CICN) is required" }}
                    render={({ field }) => (
                      <SearchComboBox
                        id={id}
                        options={childOptions}
                        value={field.value}
                        placeholder="Search by child name or enter CICN"
                        noMatchMessage="No matching children found"
                        hasError={!!errors.LocalChildId}
                        isRequired
                        ariaDescribedBy={`${id}-description`}
                        onChange={(value, selectedOption) => {
                          setValue("LocalChildId", value, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                          setValue(
                            "CSPPID",
                            selectedOption?.extra?.CSPPID ?? "",
                            {
                              shouldValidate: true,
                              shouldDirty: true,
                            }
                          );
                        }}
                      />
                    )}
                  />
                )
              }
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="LocalClassroomId"
              labelText="Classroom ID"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  readOnly
                  maxLength={50}
                  value={watchLocalClassroomId}
                  {...register(id, { required: "Classroom ID is required" })}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="ClassroomEnrollmentStart"
              labelText="Classroom Enrollment Start Date"
              errors={errors}
              tooltipMap={tooltipMap}
              tooltip={tooltipMap?.get("ClassroomStart")}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) =>
                !!selectedCLEN && !viewOnly ? (
                  <ReadOnlyDateInput
                    id={id}
                    control={control}
                    name={name}
                    rules={{
                      required: "Classroom Enrollment Start Date is required",
                      validate: validateEnrollmentStart,
                    }}
                  />
                ) : (
                  <DateInput
                    id={id}
                    disabled={viewOnly}
                    max="9999-12-31"
                    {...register(id, {
                      required: "Classroom Enrollment Start Date is required",
                      validate: validateEnrollmentStart,
                    })}
                  />
                )
              }
            </FormFieldRow>
          </div>
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="ClassroomEnrollmentEnd"
              labelText="Classroom Enrollment End Date"
              errors={errors}
              tooltipMap={tooltipMap}
              tooltip={tooltipMap?.get("ClassroomEnd")}
              labelCol={12}
              inputCol={12}
            >
              {(id) => (
                <DateInput
                  id={id}
                  disabled={viewOnly}
                  max="9999-12-31"
                  {...register(id, {
                    validate: validateEnrollmentEnd,
                  })}
                />
              )}
            </FormFieldRow>
          </div>
        </div>

        <span
          id="spnAddOrEditClassroomErrorOverall"
          className="row ps-3 mt-3 pe-3 float-end"
          style={{ color: "#CC0000" }}
        />
      </div>
    </Container>
  );
}
